import * as angular from "angular";
import swal from "sweetalert2";
import pupa from "pupa";

import "./onboarding-mode-form.less";
import template from "./onboarding-mode-form.html";
import messages from "../prompts";
import { getSubmittedRequestMessage } from "../prompts/submitted-request-message";
import { FORMS_REQUEST } from "../../../routes";
import { NOMENCLATURE } from "../../../constants";
import { isStringNullishOrEmpty } from "../../../services/string-service";
import jsonSchemaForm from "../../../extensions/json-schema/json-schema-form";
import onboardingRequestsModule from "./onboarding-requests";
import welcomeFormRequestModal from "../form-request-modal";

export function controller(
	api,
	$userProfile,
	loadingService,
	$scope,
	$uibModal,
	$sanitize,
	onboardingRequests,
	$timeout
) {
	const ctrl = this;
	let requests;
	let initialized = false;

	ctrl.$onInit = () => {
		initialized = true;
		setupForm();
	};
	ctrl.$onChanges = changes => {
		if(initialized && changes.selectedForm) {
			setupForm();
		}
	};
	ctrl.$onDestroy = () => {
		requests?.dispose();
		requests = undefined;
	};

	ctrl.validate = validate;

	ctrl.makeRequest = async(formController) => {
		const isValid = ctrl.validate(formController);
		if(!isValid) {
			return;
		}

		const request = buildRequest();
		const requestSession = requests;

		try {
			const user = $userProfile.getUser();
			await loadingService.watch(api.post(FORMS_REQUEST(ctrl.selectedForm.Id, user.databaseId), request));
			if(requestSession !== requests) {
				return;
			}
			let message = messages.submitted;

			if(ctrl.agencyPhone !== undefined && ctrl.agencyPhone.length > 0) {
				message = `${message}<br/><br/>Please call ${ctrl.agencyPhone} for request status updates.`;
			}

			swal.fire({
				title: "Request Submitted",
				html: getSubmittedRequestMessage(ctrl.selectedForm, message, $sanitize),
				showCloseButton: true,
				type: "success",
				showCancelButton: false,
				confirmButtonColor: "#8CC541",
				confirmButtonText: "Okay"
			});

			await resetSubmittedForm(formController, requestSession);
			if(requestSession !== requests) {
				return;
			}
			await loadingService.watch(requestSession.refresh(true)).catch(() => undefined);
		}
		catch(err) {
			if(requestSession !== requests) {
				return;
			}
			if(err.data?.Data?.SchemaErrors && Array.isArray(err.data.Data.SchemaErrors)) {
				const errorDetails = err.data.Data.SchemaErrors.join("<br/>");
				swal.fire({
					title: "Form Validation Error",
					html: `<p>The server rejected your submission:</p><p>${errorDetails}</p>`,
					showCloseButton: true,
					type: "error",
					showCancelButton: false,
					confirmButtonColor: "#8CC541",
					confirmButtonText: "Okay"
				});
			}
			else {
				swal.fire({
					title: "Submission Failed",
					html: "There was an error submitting your request. Please try again.",
					showCloseButton: true,
					type: "error",
					showCancelButton: false,
					confirmButtonColor: "#8CC541",
					confirmButtonText: "Okay"
				});
			}
		}
	};

	ctrl.viewRequest = (item) => {
		$uibModal.open({
			component: "welcome-form-request-modal",
			size: "lg",
			ariaLabelledBy: "welcome-form-request-modal:modal-title",
			resolve: {
				schema: () => ctrl.schema,
				request: () => item
			}
		});
	};

	function setupForm() {
		requests?.dispose();
		requests = undefined;
		ctrl.requests = [];
		if(!ctrl.selectedForm) {
			return;
		}

		ctrl.agencyPhone = ctrl.selectedForm.Nomenclature?.find(n => n.Code === NOMENCLATURE.SupportNumber)?.Text;

		const onboardingFormConfig = ctrl.selectedForm?.Onboarding?.Requests?.Form;
		const schema = resolveFormDefinition(onboardingFormConfig);

		ctrl.schema = schema;
		clearModel();
		requests = onboardingRequests.create(ctrl.selectedForm, updateRequests, { includeProcessed: true });
		loadingService.watch(requests.refresh()).catch(() => undefined);
	}

	function buildRequest() {
		return {
			Data: ctrl.model
		};
	}

	function validate(formController) {
		if(!ctrl.schema) {
			return true;
		}
		if(!formController) {
			return false;
		}

		$scope.$broadcast("schemaFormValidate", formController.$name);

		return formController.$valid;
	}

	function resolveFormDefinition(onboardingFormConfig) {
		if(!onboardingFormConfig) {
			return undefined;
		}

		if(onboardingFormConfig.type === "object" && onboardingFormConfig.properties) {
			return onboardingFormConfig;
		}

		return undefined;
	}

	function updateRequests(userRequests) {
		ctrl.requests = userRequests
			.map(request => {
				const requestTemplate = ctrl.selectedForm?.Onboarding?.Requests?.Summary;
				if(!isStringNullishOrEmpty(requestTemplate)) {
					request.display = pupa(requestTemplate, request.Data);
				}
				else if(request.Entity) {
					request.display = request.Entity;
				}
				else {
					request.display = `Request ID: ${request.Id}`;
				}

				return request;
			});
	}

	function clearModel() {
		ctrl.model = {};
		ctrl.formControl?.reset(ctrl.model);
	}

	async function resetSubmittedForm(formController, requestSession) {
		const resetState = () => {
			if(requestSession === requests) {
				formController?.$setPristine?.();
				formController?.$setUntouched?.();
			}
		};
		resetState();
		clearModel();
		// Let the cleared model render before resetting after its queued publication.
		await $timeout();
		await $timeout(resetState);
	}
}

export default angular
	.module("vesl.screens.welcome.onboarding-mode-form", [jsonSchemaForm, onboardingRequestsModule, welcomeFormRequestModal])
	.component("onboardingModeForm", {
		bindings: {
			selectedForm: "<"
		},
		controller,
		template
	}).name;
